import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import CtaButtons from "@/components/seo/CtaButtons";

const serif = { fontFamily: "'Playfair Display', serif" };

export const SeoHero = ({ crumbs, eyebrow, h1, intro }) => (
  <section className="bg-[#0F172A] text-white pt-28 md:pt-32 pb-12 md:pb-16" data-testid="seo-hero">
    <div className="px-4 md:px-8 lg:px-16 max-w-6xl mx-auto">
      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-[11px] md:text-xs uppercase tracking-widest text-slate-400 mb-6" data-testid="breadcrumb">
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="w-3 h-3 text-[#D4AF37]" />}
            {c.to ? <Link to={c.to} className="hover:text-[#D4AF37] transition-colors">{c.label}</Link> : <span className="text-slate-200">{c.label}</span>}
          </span>
        ))}
      </nav>
      <p className="text-[#D4AF37] text-xs md:text-sm uppercase tracking-widest font-semibold mb-3">{eyebrow}</p>
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5" style={serif} data-testid="seo-h1">{h1}</h1>
      <p className="text-slate-300 text-sm md:text-base leading-relaxed max-w-3xl mb-8">{intro}</p>
      <CtaButtons dark testPrefix="hero-cta" />
    </div>
  </section>
);

export const SeoSection = ({ title, children, alt = false, id, testId }) => (
  <section id={id} className={`py-12 md:py-16 ${alt ? "bg-slate-50" : "bg-white"}`} data-testid={testId}>
    <div className="px-4 md:px-8 lg:px-16 max-w-6xl mx-auto">
      {title && (
        <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-6 border-l-4 border-[#D4AF37] pl-4" style={serif}>{title}</h2>
      )}
      {children}
    </div>
  </section>
);

export const InfoCards = ({ items, numbered = false }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
    {items.map((it, i) => (
      <div key={i} className="bg-white border border-slate-200 border-l-4 border-l-[#D4AF37] p-5 md:p-6 hover:shadow-lg transition-shadow duration-300">
        <h3 className="text-base md:text-lg font-semibold text-[#0F172A] mb-2" style={serif}>
          {numbered && <span className="text-[#D4AF37] mr-2">{i + 1}.</span>}{it.title}
        </h3>
        <p className="text-sm md:text-base text-slate-600 leading-relaxed">{it.text}</p>
      </div>
    ))}
  </div>
);

export const FaqList = ({ items }) => (
  <div className="space-y-4">
    {items.map((f, i) => (
      <div key={i} className="bg-white border border-slate-200 p-5 md:p-6" data-testid={`faq-item-${i}`}>
        <h3 className="text-base md:text-lg font-semibold text-[#0F172A] mb-2" style={serif}>{f.q}</h3>
        <p className="text-sm md:text-base text-slate-600 leading-relaxed">{f.a}</p>
      </div>
    ))}
  </div>
);

export const LinkChips = ({ items, testId }) => (
  <div className="flex flex-wrap gap-2 md:gap-3" data-testid={testId}>
    {items.map((it, i) =>
      it.to ? (
        <Link key={i} to={it.to} className="inline-flex items-center gap-1 bg-white border border-slate-300 text-[#0F172A] px-4 py-2 text-xs md:text-sm hover:border-[#D4AF37] hover:text-[#B5952F] transition-colors duration-300">
          {it.label} <ChevronRight className="w-3 h-3 text-[#D4AF37]" />
        </Link>
      ) : (
        <span key={i} className="inline-flex items-center bg-slate-100 text-slate-700 px-4 py-2 text-xs md:text-sm">{it.label}</span>
      )
    )}
  </div>
);

export const CtaBand = ({ title, text }) => (
  <section className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white py-12 md:py-16" data-testid="cta-band">
    <div className="px-4 md:px-8 lg:px-16 max-w-6xl mx-auto">
      <h2 className="text-2xl md:text-3xl font-bold mb-3" style={serif}>{title}</h2>
      <p className="text-slate-300 text-sm md:text-base mb-6 max-w-2xl">{text}</p>
      <CtaButtons dark testPrefix="band-cta" />
    </div>
  </section>
);
